import React, { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { BarChart3, GraduationCap, Users, Zap, Globe, AlertTriangle, TrendingUp } from 'lucide-react';
import ExportBar from '../components/ExportBar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Governance() {
  const { t, role, lang } = useApp();
  const [dashboard, setDashboard] = useState(null);
  const [sdg4, setSdg4] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dashRes = await axios.get(`${API}/governance/dashboard`, { params: { role } });
        setDashboard(dashRes.data);
        // Alerts come from dashboard response OR separate endpoint
        if (dashRes.data.alerts) setAlerts(dashRes.data.alerts);
        else {
          const alertRes = await axios.get(`${API}/governance/alerts`);
          setAlerts(Array.isArray(alertRes.data) ? alertRes.data : alertRes.data.alerts || []);
        }
      } catch(e) { /* error handled gracefully */ }
      // SDG4 — only for district+ roles
      if (role === 'district' || role === 'moe') {
        try {
          const sdg4Res = await axios.get(`${API}/governance/sdg4`);
          setSdg4(sdg4Res.data);
        } catch(e) { /* expected 403 for non-district roles */ }
      }
      setLoading(false);
    };
    fetchData();
  }, [role]);

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/governance/alerts/${alertId}?status=resolved`);
      setAlerts(prev => prev.map(a => a.alert_id === alertId ? {...a, status: 'resolved'} : a));
    } catch(e) {}
  };

  if (loading) return <div className="text-center py-12 text-slate-400">{t('loading')}</div>;

  const tabs = [
    { id: 'overview', label: lang === 'bn' ? 'সারসংক্ষেপ' : 'Overview' },
    { id: 'sdg4', label: lang === 'bn' ? 'SDG4 সূচক' : 'SDG4 Indicators' },
    { id: 'alerts', label: lang === 'bn' ? 'সতর্কতা' : `Alerts (${alerts.filter(a => a.status === 'open').length})` },
    { id: 'alert_mgmt', label: lang === 'bn' ? 'সতর্কতা ব্যবস্থাপনা' : 'Alert Management' },
    ...(role === 'moe' ? [{ id: 'national', label: lang === 'bn' ? 'জাতীয় ড্যাশবোর্ড' : 'National Dashboard' }] : []),
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="governance-dashboard">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`gov-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab.id ? 'text-[#1B2A4A] border-[#1B2A4A]' : 'text-slate-400 border-transparent hover:text-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-value">{dashboard.total_schools || 0}</p>
                  <p className="stat-label">{lang === 'bn' ? 'বিদ্যালয়' : 'Schools'}</p>
                  <p style={{fontSize:'11px',opacity:0.4,marginTop:2}}>{lang === 'bn' ? 'Schools' : 'বিদ্যালয়'}</p>
                </div>
                <BarChart3 size={24} style={{color: 'var(--color-accent)'}} />
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-value" style={{color: 'var(--color-accent)'}}>{dashboard.attendance_rate || 0}%</p>
                  <p className="stat-label">{lang === 'bn' ? 'উপস্থিতির হার' : 'Attendance Rate'}</p>
                  <p style={{fontSize:'11px',opacity:0.4,marginTop:2}}>{lang === 'bn' ? 'Attendance Rate' : 'উপস্থিতির হার'}</p>
                </div>
                <TrendingUp size={24} style={{color: 'var(--color-accent)'}} />
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-value" style={{color: 'var(--color-warning)'}}>{dashboard.lms_compliance || dashboard.meal_compliance || 0}%</p>
                  <p className="stat-label">{lang === 'bn' ? 'LMS সম্মতি' : 'LMS Compliance'}</p>
                  <p style={{fontSize:'11px',opacity:0.4,marginTop:2}}>{lang === 'bn' ? 'LMS Compliance' : 'LMS সম্মতি'}</p>
                </div>
                <Zap size={24} style={{color: 'var(--color-warning)'}} />
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-value" style={{color: 'var(--color-error)'}}>{alerts.filter(a => a.status === 'open').length}</p>
                  <p className="stat-label">{lang === 'bn' ? 'সক্রিয় সতর্কতা' : 'Open Alerts'}</p>
                  <p style={{fontSize:'11px',opacity:0.4,marginTop:2}}>{lang === 'bn' ? 'Open Alerts' : 'সক্রিয় সতর্কতা'}</p>
                </div>
                <AlertTriangle size={24} style={{color: 'var(--color-error)'}} />
              </div>
            </div>
          </div>

          <div className="card-surface">
            <h3 style={{fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-5)'}}>
              {role === 'principal' ? (lang === 'bn' ? 'শিক্ষক কর্মক্ষমতা' : 'Teacher Performance') : (lang === 'bn' ? 'বিদ্যালয় তুলনা' : 'Cross-School Comparison')}
            </h3>
            <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'var(--space-3)'}}>
              <ExportBar title="Governance School Comparison" data={dashboard?.schools || []} columns={[
                {label:'School',accessor:r => lang==='bn' ? r.name_bangla : (r.name_english||r.name_bangla)},
                {label:'District',accessor:'district'},{label:'Teachers',accessor:'total_teachers'},
                {label:'Students',accessor:'total_students'},{label:'Score',accessor:'composite_score'}
              ]} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full data-table" data-testid="governance-school-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{lang === 'bn' ? 'বিদ্যালয়' : 'School'}</th>
                    <th>{lang === 'bn' ? 'জেলা' : 'District'}</th>
                    <th>{lang === 'bn' ? 'শিক্ষক' : 'Teachers'}</th>
                    <th>{lang === 'bn' ? 'শিক্ষার্থী' : 'Students'}</th>
                    <th>{lang === 'bn' ? 'স্কোর' : 'Score'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboard.schools || []).sort((a,b) => (b.composite_score||0) - (a.composite_score||0)).map((s, i) => (
                    <tr key={s.school_id}>
                      <td className="font-medium">{i+1}</td>
                      <td>{lang === 'bn' ? s.name_bangla : (s.name_english || s.name_bangla)}</td>
                      <td>{s.district}</td>
                      <td>{s.total_teachers}</td>
                      <td>{s.total_students}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${s.composite_score}%`,
                              backgroundColor: s.composite_score >= 75 ? '#1D9E75' : s.composite_score >= 60 ? '#C9A84C' : '#C00000'
                            }} />
                          </div>
                          <span className="text-xs font-medium">{s.composite_score}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* SDG4 Tab — BANBEIS 2024 Data */}
      {activeTab === 'sdg4' && (
        <div className="space-y-6">
          {/* Source attribution */}
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', display: 'inline-block' }}>
            {lang === 'bn' ? 'সূত্র: বাংলাদেশ শিক্ষাতথ্য ও পরিসংখ্যান ব্যুরো (BANBEIS) ২০২৪, মার্চ ২০২৫ প্রকাশিত' : 'Source: Bangladesh Bureau of Educational Information & Statistics (BANBEIS) 2024, released March 2025'}
          </div>

          {/* Top-level KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="stat-card">
              <p className="stat-value" style={{ color: 'var(--color-accent)' }}>37,489</p>
              <div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'মোট প্রতিষ্ঠান (মাধ্যমিক+)' : 'Total Institutions (Post-Primary)'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Institutions' : 'মোট প্রতিষ্ঠান'}</span></div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-2)' }}>2,023 public | 35,466 private</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">667,099</p>
              <div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'মোট শিক্ষক' : 'Total Teachers'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Teachers' : 'মোট শিক্ষক'}</span></div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-2)' }}>29.10% {lang === 'bn' ? 'মহিলা শিক্ষক' : 'female teachers'}</p>
            </div>
            <div className="stat-card">
              <p className="stat-value" style={{ color: 'var(--color-accent)' }}>{sdg4 ? sdg4.gender_parity_index : '0.52'}</p>
              <div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'লিঙ্গ সমতা সূচক' : 'Gender Parity Index'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Gender Parity Index' : 'লিঙ্গ সমতা সূচক'}</span></div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-2)' }}>Target: 1.0 | SDG 4.5</p>
            </div>
            <div className="stat-card">
              <p className="stat-value" style={{ color: 'var(--color-warning)' }}>17.4M+</p>
              <div className="bi-label"><span className="stat-label">{lang === 'bn' ? 'মোট শিক্ষার্থী (মাধ্যমিক+)' : 'Total Students (Post-Primary)'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Students' : 'মোট শিক্ষার্থী'}</span></div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 'var(--space-2)' }}>54.92% {lang === 'bn' ? 'মেয়ে (মাধ্যমিক)' : 'female (secondary)'}</p>
            </div>
          </div>

          {/* Institution Breakdown — BANBEIS 2024 */}
          <div className="card-surface">
            <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>
              {lang === 'bn' ? 'প্রতিষ্ঠান, শিক্ষক ও শিক্ষার্থী বিভাজন (BANBEIS ২০২৪)' : 'Institution, Teacher & Student Breakdown (BANBEIS 2024)'}
              <span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Breakdown by Institution Type' : 'প্রতিষ্ঠান ধরন অনুযায়ী বিভাজন'}</span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full data-table" data-testid="sdg4-banbeis-table">
                <thead>
                  <tr>
                    <th>{lang === 'bn' ? 'প্রতিষ্ঠানের ধরন' : 'Institution Type'}</th>
                    <th>{lang === 'bn' ? 'প্রতিষ্ঠান সংখ্যা' : 'Institutions'}</th>
                    <th>{lang === 'bn' ? 'শিক্ষক সংখ্যা' : 'Teachers'}</th>
                    <th>{lang === 'bn' ? 'মহিলা শিক্ষক %' : 'Female Teachers %'}</th>
                    <th>{lang === 'bn' ? 'শিক্ষার্থী সংখ্যা' : 'Students'}</th>
                    <th>{lang === 'bn' ? 'মেয়ে শিক্ষার্থী %' : 'Female Students %'}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { type: 'Secondary School', typeBn: 'মাধ্যমিক বিদ্যালয়', inst: '19,117', pub: 631, teachers: '258,944', femTeacher: 30.34, students: '7,847,546', femStudent: 54.92 },
                    { type: 'English Medium School', typeBn: 'ইংরেজি মাধ্যম বিদ্যালয়', inst: '147', pub: 0, teachers: '7,178', femTeacher: 71.68, students: '29,077', femStudent: 45.58 },
                    { type: 'School & College', typeBn: 'স্কুল ও কলেজ', inst: '1,514', pub: 63, teachers: '57,591', femTeacher: 35.82, students: '1,564,880', femStudent: 53.39 },
                    { type: 'College Education', typeBn: 'কলেজ শিক্ষা', inst: '3,362', pub: 642, teachers: '109,543', femTeacher: 27.90, students: '4,492,849', femStudent: 51.28 },
                    { type: 'Madrasah Education', typeBn: 'মাদ্রাসা শিক্ষা', inst: '9,269', pub: 3, teachers: '128,522', femTeacher: 20.84, students: '2,796,191', femStudent: 52.90 },
                    { type: 'Technical-Vocational', typeBn: 'কারিগরি-বৃত্তিমূলক', inst: '2,827', pub: 396, teachers: '29,631', femTeacher: 28.35, students: '676,240', femStudent: 27.33 },
                    { type: 'Professional Education', typeBn: 'পেশাগত শিক্ষা', inst: '870', pub: 137, teachers: '17,464', femTeacher: 45.42, students: '—', femStudent: null },
                    { type: 'Teacher Education', typeBn: 'শিক্ষক শিক্ষা', inst: '209', pub: 94, teachers: '2,843', femTeacher: 30.11, students: '—', femStudent: null },
                    { type: 'University Education', typeBn: 'বিশ্ববিদ্যালয় শিক্ষা', inst: '174', pub: 57, teachers: '32,917', femTeacher: 29.11, students: '—', femStudent: null },
                  ].map((row, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{lang === 'bn' ? row.typeBn : row.type}</td>
                      <td>{row.inst} <span style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>({row.pub} {lang === 'bn' ? 'সরকারি' : 'public'})</span></td>
                      <td>{row.teachers}</td>
                      <td><span className={"badge " + (row.femTeacher >= 40 ? 'badge-success' : row.femTeacher >= 25 ? 'badge-accent' : 'badge-warning')}>{row.femTeacher}%</span></td>
                      <td>{row.students}</td>
                      <td>{row.femStudent !== null ? <span className={"badge " + (row.femStudent >= 50 ? 'badge-success' : 'badge-warning')}>{row.femStudent}%</span> : '—'}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700, background: 'var(--color-accent-light)' }}>
                    <td>{lang === 'bn' ? 'মোট (মাধ্যমিক+)' : 'TOTAL (Post-Primary)'}</td>
                    <td>37,489 <span style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>(2,023 {lang === 'bn' ? 'সরকারি' : 'public'})</span></td>
                    <td>667,099</td>
                    <td><span className="badge badge-accent">29.10%</span></td>
                    <td>17.4M+</td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional SDG4 Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="card-surface">
              <h4 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {lang === 'bn' ? 'অবকাঠামো সূচক' : 'Infrastructure Indicators'}
                <span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'Infrastructure' : 'অবকাঠামো'}</span>
              </h4>
              {[
                { en: 'Attached Technical Institutions', bn: 'সংযুক্ত কারিগরি প্রতিষ্ঠান', value: '4,013', sub: '614 public' },
                { en: 'Upgraded Govt. Primary Schools', bn: 'উন্নীত সরকারি প্রাথমিক বিদ্যালয়', value: '601', sub: 'All government' },
                { en: 'Training Institutions', bn: 'প্রশিক্ষণ প্রতিষ্ঠান', value: '2,470', sub: '91 public' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-3) 0', borderBottom: i < 2 ? '1px solid var(--color-separator)' : 'none' }}>
                  <div><span style={{ fontSize: 'var(--text-subhead)' }}>{lang === 'bn' ? item.bn : item.en}</span><span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{lang === 'bn' ? item.en : item.bn}</span></div>
                  <div style={{ textAlign: 'right' }}><span style={{ fontSize: 'var(--text-body)', fontWeight: 700, color: 'var(--color-accent)' }}>{item.value}</span><span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 1 }}>{item.sub}</span></div>
                </div>
              ))}
            </div>
            <div className="card-surface">
              <h4 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {lang === 'bn' ? 'SDG 4 লক্ষ্যমাত্রা অগ্রগতি' : 'SDG 4 Target Progress'}
                <span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'SDG 4 Progress' : 'SDG 4 অগ্রগতি'}</span>
              </h4>
              {[
                { target: '4.1', en: 'Quality Primary & Secondary Education', progress: 78 },
                { target: '4.3', en: 'Equal Access to Technical/Vocational Education', progress: 45 },
                { target: '4.4', en: 'Skills for Employment', progress: 52 },
                { target: '4.5', en: 'Gender Equality in Education', progress: 85 },
                { target: '4.6', en: 'Universal Literacy & Numeracy', progress: 74 },
                { target: '4.c', en: 'Qualified Teachers Supply', progress: 62 },
              ].map((sdg, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) 0' }}>
                  <span style={{ fontSize: 'var(--text-footnote)', fontWeight: 700, color: 'var(--color-accent)', width: 28 }}>{sdg.target}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 'var(--text-footnote)' }}>{sdg.en}</span>
                    <div style={{ height: 4, background: 'var(--color-surface-2)', borderRadius: 99, marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${sdg.progress}%`, height: '100%', background: sdg.progress >= 75 ? 'var(--color-accent)' : sdg.progress >= 50 ? 'var(--color-warning)' : 'var(--color-error)', borderRadius: 99 }} />
                    </div>
                  </div>
                  <span style={{ fontSize: 'var(--text-footnote)', fontWeight: 600, width: 36, textAlign: 'right' }}>{sdg.progress}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* District comparison from API */}
          {sdg4 && sdg4.districts && sdg4.districts.length > 0 && (
            <div className="card-surface">
              <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>{lang === 'bn' ? 'জেলা তুলনা' : 'District Comparison'}</h3>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead><tr><th>{lang === 'bn' ? 'জেলা' : 'District'}</th><th>{lang === 'bn' ? 'বিদ্যালয়' : 'Schools'}</th><th>{lang === 'bn' ? 'শিক্ষার্থী' : 'Students'}</th><th>{lang === 'bn' ? 'শিক্ষক' : 'Teachers'}</th><th>{lang === 'bn' ? 'শি:শিক্ষক অনুপাত' : 'S:T Ratio'}</th><th>{lang === 'bn' ? 'বিদ্যুৎ' : 'Electricity'}</th><th>{lang === 'bn' ? 'ইন্টারনেট' : 'Internet'}</th></tr></thead>
                  <tbody>{sdg4.districts.map(d => (
                    <tr key={d.district}><td style={{ fontWeight: 500 }}>{d.district}</td><td>{d.schools}</td><td>{d.students?.toLocaleString()}</td><td>{d.teachers}</td><td>{d.student_teacher_ratio}:1</td>
                      <td><span className={d.electricity_pct >= 80 ? 'badge badge-success' : 'badge badge-warning'}>{d.electricity_pct}%</span></td>
                      <td><span className={d.internet_pct >= 50 ? 'badge badge-success' : 'badge badge-error'}>{d.internet_pct}%</span></td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {alerts.map(alert => (
            <div key={alert.alert_id} className={`card-flat p-4 ${alert.status === 'open' ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-emerald-500 opacity-60'}`}
              data-testid={`alert-${alert.alert_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      alert.severity >= 4 ? 'bg-red-100 text-red-700' : alert.severity >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {alert.alert_type}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${alert.status === 'open' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {alert.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">{lang === 'bn' ? alert.detail_bangla : (alert.detail_english || alert.detail_bangla)}</p>
                  {alert.school_name && <p className="text-xs text-slate-400 mt-1">{lang === 'bn' ? alert.school_name : (alert.school_name_english || alert.school_name)}</p>}
                  <p className="text-[10px] text-slate-300 mt-1">{new Date(alert.created_at).toLocaleDateString()}</p>
                </div>
                {alert.status === 'open' && (
                  <button
                    data-testid={`resolve-alert-${alert.alert_id}`}
                    onClick={() => resolveAlert(alert.alert_id)}
                    className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors"
                  >
                    {lang === 'bn' ? 'সমাধান' : 'Resolve'}
                  </button>
                )}
              </div>
            </div>
          ))}
          {alerts.length === 0 && <p className="text-center text-sm text-slate-400 py-8">{lang === 'bn' ? 'কোনো সতর্কতা নেই' : 'No alerts'}</p>}
        </div>
      )}

      {/* ALERT MANAGEMENT */}
      {activeTab === 'alert_mgmt' && (
        <div className="space-y-4">
          <div className="card-surface" style={{padding:'var(--space-5)'}}>
            <h3 style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? 'সতর্কতা ব্যবস্থাপনা সিস্টেম' : 'Alert Management System'}</h3>
            <div style={{fontSize:11,color:'var(--color-label-tertiary)',marginTop:2}}>{lang === 'bn' ? 'Alert Management System' : 'সতর্কতা ব্যবস্থাপনা সিস্টেম'}</div>
            <p style={{fontSize:'var(--text-subhead)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)',lineHeight:1.6}}>
              {lang === 'bn' ? 'সতর্কতা তৈরি → অগ্রাধিকার নির্ধারণ → দায়িত্বপ্রাপ্তকে বরাদ্দ → তদন্ত → সমাধান → বন্ধ' : 'Alert Created → Priority Set → Assigned to Owner → Investigation → Resolution → Closed'}
            </p>
          </div>
          {/* Alert Pipeline */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[{status:'Open',statusBn:'সক্রিয়',count:8,color:'var(--color-error)'},{status:'Investigating',statusBn:'তদন্ত চলছে',count:3,color:'var(--color-warning)'},{status:'Resolved',statusBn:'সমাধান হয়েছে',count:12,color:'var(--color-accent)'},{status:'Closed',statusBn:'বন্ধ',count:45,color:'var(--color-label-tertiary)'}].map(s => (
              <div key={s.status} className="stat-card" style={{textAlign:'center'}}>
                <p className="stat-value" style={{color:s.color}}>{s.count}</p>
                <div className="bi-label"><span className="stat-label">{lang === 'bn' ? s.statusBn : s.status}</span><span style={{fontSize:10,opacity:0.4,marginTop:2}}>{lang === 'bn' ? s.status : s.statusBn}</span></div>
              </div>
            ))}
          </div>
          {/* Alert Details */}
          {[
            {type:'MEAL_FRAUD',en:'Beneficiary count exceeds enrollment — Dhaka Model School',bn:'উপকারভোগী সংখ্যা বেশি — ঢাকা মডেল বিদ্যালয়',severity:4,status:'open',owner:'District Inspector',escalation:'Auto-escalated after 48hrs',created:'2 days ago'},
            {type:'ATTENDANCE_ANOMALY',en:'100% attendance for 15 days — statistically improbable',bn:'১৫ দিনে ১০০% উপস্থিতি — পরিসংখ্যানগত অসম্ভব',severity:3,status:'investigating',owner:'Principal — Mirpur School',escalation:'Under review',created:'3 days ago'},
            {type:'TEACHER_ABSENCE',en:'2 teachers absent 5+ days without leave approval',bn:'২ জন শিক্ষক ৫+ দিন বিনা ছুটিতে অনুপস্থিত',severity:2,status:'investigating',owner:'School Principal',escalation:'Escalate to District in 24hrs',created:'5 days ago'},
            {type:'INFRASTRUCTURE',en:'School electricity disconnected — 3 days',bn:'বিদ্যালয়ে বিদ্যুৎ বিচ্ছিন্ন — ৩ দিন',severity:4,status:'open',owner:'District Admin',escalation:'MOE notification sent',created:'3 days ago'},
            {type:'STIPEND_DELAY',en:'Stipend disbursement delayed 2 weeks — 45 students affected',bn:'বৃত্তি বিতরণ ২ সপ্তাহ বিলম্বিত',severity:2,status:'resolved',owner:'Finance Dept.',escalation:'Disbursement completed',created:'1 week ago'},
            {type:'LMS_COMPLIANCE',en:'8 teachers missed mandatory CPD deadline',bn:'৮ জন শিক্ষক বাধ্যতামূলক CPD সময়সীমা মিস করেছেন',severity:3,status:'open',owner:'NAEM Coordinator',escalation:'Reminder sent',created:'1 day ago'},
          ].map((alert,i) => (
            <div key={i} className="card-surface" style={{borderLeft:`3px solid ${alert.status==='open'?'var(--color-error)':alert.status==='investigating'?'var(--color-warning)':'var(--color-accent)'}`,padding:'var(--space-4) var(--space-5)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',gap:'var(--space-2)',flexWrap:'wrap',marginBottom:'var(--space-2)'}}>
                    <span className="badge badge-neutral" style={{fontSize:9}}>{alert.type}</span>
                    <span className={"badge " + (alert.severity>=4?'badge-error':alert.severity>=3?'badge-warning':'badge-neutral')}>SEV-{alert.severity}</span>
                    <span className={"badge " + (alert.status==='open'?'badge-error':alert.status==='investigating'?'badge-warning':'badge-success')}>{alert.status.toUpperCase()}</span>
                  </div>
                  <div style={{fontSize:'var(--text-body)',fontWeight:600}}>{lang === 'bn' ? alert.bn : alert.en}</div>
                  <div style={{fontSize:10,color:'var(--color-label-tertiary)',marginTop:1}}>{lang === 'bn' ? alert.en : alert.bn}</div>
                  <div style={{fontSize:'var(--text-footnote)',color:'var(--color-label-secondary)',marginTop:'var(--space-2)'}}>
                    {lang === 'bn' ? 'দায়িত্বপ্রাপ্ত' : 'Owner'}: {alert.owner} | {alert.created}
                  </div>
                  <div style={{fontSize:'var(--text-caption)',color:'var(--color-label-tertiary)',marginTop:2}}>
                    {lang === 'bn' ? 'এসকেলেশন' : 'Escalation'}: {alert.escalation}
                  </div>
                </div>
                {alert.status !== 'resolved' && (
                  <div style={{display:'flex',flexDirection:'column',gap:'var(--space-2)',flexShrink:0,marginLeft:'var(--space-4)'}}>
                    <button className="btn btn-sm" style={{background:'var(--color-accent)',color:'white',padding:'4px 10px',fontSize:11}}>{lang === 'bn' ? 'আপডেট' : 'Update'}</button>
                    {alert.status === 'open' && <button className="btn btn-sm" style={{background:'var(--color-warning-bg)',color:'var(--color-warning)',padding:'4px 10px',fontSize:11}}>{lang === 'bn' ? 'এসকেলেট' : 'Escalate'}</button>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* National Dashboard (MoE only) */}
      {activeTab === 'national' && (
        <div className="space-y-6">
          <div className="card-surface" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
            <Globe size={48} style={{ color: 'var(--color-accent)', margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: 'var(--text-title3)', fontWeight: 700, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? 'জাতীয় শিক্ষা ড্যাশবোর্ড' : 'National Education Dashboard'}</h3>
            <p style={{ fontSize: 11, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'National Education Dashboard' : 'জাতীয় শিক্ষা ড্যাশবোর্ড'}</p>
            <p style={{ fontSize: 'var(--text-subhead)', color: 'var(--color-label-secondary)', marginTop: 'var(--space-2)' }}>Bangladesh Ministry of Education — 64 Districts | BANBEIS 2024</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="stat-card" style={{ textAlign: 'center' }}>
              <p className="stat-value" style={{ fontSize: 44 }}>37,489</p>
              <div className="bi-label" style={{ justifyContent: 'center' }}><span className="stat-label">{lang === 'bn' ? 'মোট প্রতিষ্ঠান (মাধ্যমিক+)' : 'Total Institutions (Post-Primary)'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Institutions' : 'মোট প্রতিষ্ঠান'}</span></div>
            </div>
            <div className="stat-card" style={{ textAlign: 'center' }}>
              <p className="stat-value" style={{ fontSize: 44, color: 'var(--color-accent)' }}>667,099</p>
              <div className="bi-label" style={{ justifyContent: 'center' }}><span className="stat-label">{lang === 'bn' ? 'মোট শিক্ষক' : 'Total Teachers'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Teachers' : 'মোট শিক্ষক'}</span></div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--color-label-tertiary)', marginTop: 4 }}>29.10% female</p>
            </div>
            <div className="stat-card" style={{ textAlign: 'center' }}>
              <p className="stat-value" style={{ fontSize: 44, color: 'var(--color-warning)' }}>17.4M+</p>
              <div className="bi-label" style={{ justifyContent: 'center' }}><span className="stat-label">{lang === 'bn' ? 'মোট শিক্ষার্থী (মাধ্যমিক+)' : 'Total Students (Post-Primary)'}</span><span style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>{lang === 'bn' ? 'Total Students' : 'মোট শিক্ষার্থী'}</span></div>
            </div>
          </div>
          {sdg4 && sdg4.districts && sdg4.districts.length > 0 && (
            <div className="card-surface">
              <h3 style={{ fontSize: 'var(--text-subhead)', fontWeight: 600, color: 'var(--color-label-secondary)', marginBottom: 'var(--space-4)' }}>{lang === 'bn' ? 'জেলা কর্মক্ষমতা' : 'District Performance'}<span style={{ display: 'block', fontSize: 10, color: 'var(--color-label-tertiary)', marginTop: 2 }}>{lang === 'bn' ? 'District Performance' : 'জেলা কর্মক্ষমতা'}</span></h3>
              <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={250}>
                <BarChart data={sdg4.districts}><XAxis dataKey="district" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="students" fill="var(--color-accent)" name={lang === 'bn' ? 'শিক্ষার্থী' : 'Students'} /><Bar dataKey="teachers" fill="var(--color-warning)" name={lang === 'bn' ? 'শিক্ষক' : 'Teachers'} /></BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
